import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useBooks } from '../context/BookContext';
import { WOOD_THEMES, DEFAULT_WOOD_THEME_KEY } from '../themes/woodThemes';
import { Plus, Pencil, Trash2, MapPin, X, Save, Search, ArrowUp, ArrowDown, MoveRight } from 'lucide-react';

// Real, named, user-created shelves -- independent of the status tabs
// (TBR/Reading/Read/Owned) in Library.js. Each book can live on at most
// one of these (books.shelf_id), completely separate from its status.
// Reuses the WOOD_THEMES palette (src/themes/woodThemes.js) already used
// by Bookshelf.js/TbrTableStack.js so shelves visually match the rest of
// the app's furniture theming.

const emptyDraft = { name: '', style_key: DEFAULT_WOOD_THEME_KEY, location_label: '' };

const MyShelves = ({ onBookClick }) => {
  const { user } = useAuth();
  const { books, updateBook } = useBooks();
  const [shelves, setShelves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null); // null = not editing, 'new' = creating
  const [draft, setDraft] = useState(emptyDraft);
  const [activeShelfId, setActiveShelfId] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [moveMenuBookId, setMoveMenuBookId] = useState(null);

  const fetchShelves = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from('shelves')
      .select('*')
      .eq('user_id', user.id)
      .order('sort_order', { ascending: true, nullsFirst: false })
      .order('created_at', { ascending: true });
    if (!error && data) {
      setShelves(data);
      setActiveShelfId(prev => prev && data.some(s => s.id === prev) ? prev : (data[0]?.id || null));
    }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchShelves(); }, [fetchShelves]);

  // Debounce shelf-book search same as the collection-search pattern in Library.js
  useEffect(() => {
    const handle = setTimeout(() => setSearchTerm(searchInput.trim().toLowerCase()), 250);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const startCreate = () => {
    setDraft({ ...emptyDraft, style_key: DEFAULT_WOOD_THEME_KEY });
    setEditingId('new');
  };

  const startEdit = (shelf) => {
    setDraft({ name: shelf.name, style_key: shelf.style_key || DEFAULT_WOOD_THEME_KEY, location_label: shelf.location_label || '' });
    setEditingId(shelf.id);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(emptyDraft);
  };

  const saveDraft = async () => {
    if (!user || !draft.name.trim()) return;
    if (editingId === 'new') {
      const { data, error } = await supabase
        .from('shelves')
        .insert([{
          user_id: user.id,
          name: draft.name.trim(),
          style_key: draft.style_key,
          location_label: draft.location_label.trim() || null,
          sort_order: shelves.length
        }])
        .select();
      if (!error && data) {
        setShelves(prev => [...prev, data[0]]);
        setActiveShelfId(data[0].id);
      }
    } else {
      const { error } = await supabase
        .from('shelves')
        .update({
          name: draft.name.trim(),
          style_key: draft.style_key,
          location_label: draft.location_label.trim() || null
        })
        .eq('id', editingId);
      if (!error) {
        setShelves(prev => prev.map(s => s.id === editingId ? { ...s, name: draft.name.trim(), style_key: draft.style_key, location_label: draft.location_label.trim() || null } : s));
      }
    }
    cancelEdit();
  };

  const deleteShelf = async (shelfId) => {
    if (!window.confirm('Delete this shelf? Books on it will just become unassigned (they stay in your library).')) return;
    const { error } = await supabase.from('shelves').delete().eq('id', shelfId);
    if (!error) {
      setShelves(prev => prev.filter(s => s.id !== shelfId));
      if (activeShelfId === shelfId) setActiveShelfId(null);
    }
  };

  // Reorder shelves via the existing (previously unused) sort_order column.
  const moveShelf = async (shelfId, direction) => {
    const index = shelves.findIndex(s => s.id === shelfId);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapIndex < 0 || swapIndex >= shelves.length) return;

    const reordered = [...shelves];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    setShelves(reordered);

    const a = reordered[index];
    const b = reordered[swapIndex];
    await Promise.all([
      supabase.from('shelves').update({ sort_order: index }).eq('id', a.id),
      supabase.from('shelves').update({ sort_order: swapIndex }).eq('id', b.id)
    ]);
  };

  const moveBookToShelf = async (bookId, targetShelfId) => {
    await updateBook(bookId, { shelf_id: targetShelfId });
    setMoveMenuBookId(null);
  };

  const activeShelf = shelves.find(s => s.id === activeShelfId);
  const activeTheme = WOOD_THEMES[activeShelf?.style_key] || WOOD_THEMES[DEFAULT_WOOD_THEME_KEY];
  const allShelfBooks = activeShelf ? books.filter(b => b.shelf_id === activeShelf.id) : [];
  const shelfBooks = searchTerm
    ? allShelfBooks.filter(b => (b.title || '').toLowerCase().includes(searchTerm) || (b.author || '').toLowerCase().includes(searchTerm))
    : allShelfBooks;

  if (loading) return <div style={{ padding: '20px', color: 'var(--text-soft)' }}>Loading shelves…</div>;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginBottom: '14px' }}>
        {shelves.map((shelf, i) => (
          <div key={shelf.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
            <button
              onClick={() => setActiveShelfId(shelf.id)}
              style={{
                padding: '6px 14px',
                borderRadius: '14px',
                border: '1px solid ' + (shelf.id === activeShelfId ? 'var(--accent)' : 'var(--border)'),
                backgroundColor: (WOOD_THEMES[shelf.style_key] || WOOD_THEMES[DEFAULT_WOOD_THEME_KEY]).surface,
                color: (WOOD_THEMES[shelf.style_key] || WOOD_THEMES[DEFAULT_WOOD_THEME_KEY]).spineText,
                fontWeight: shelf.id === activeShelfId ? 'bold' : 'normal',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              {shelf.name}
            </button>
            {shelf.id === activeShelfId && shelves.length > 1 && (
              <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
                <button
                  onClick={() => moveShelf(shelf.id, 'up')}
                  disabled={i === 0}
                  title="Move shelf earlier"
                  style={{ border: 'none', background: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--border)' : 'var(--text-soft)', padding: 0, lineHeight: 0 }}
                >
                  <ArrowUp size={12} />
                </button>
                <button
                  onClick={() => moveShelf(shelf.id, 'down')}
                  disabled={i === shelves.length - 1}
                  title="Move shelf later"
                  style={{ border: 'none', background: 'none', cursor: i === shelves.length - 1 ? 'default' : 'pointer', color: i === shelves.length - 1 ? 'var(--border)' : 'var(--text-soft)', padding: 0, lineHeight: 0 }}
                >
                  <ArrowDown size={12} />
                </button>
              </span>
            )}
          </div>
        ))}
        <button
          onClick={startCreate}
          style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 12px', borderRadius: '14px',
            border: '1px dashed var(--border)', backgroundColor: 'transparent',
            color: 'var(--text-soft)', cursor: 'pointer', fontSize: '0.85rem'
          }}
        >
          <Plus size={14} /> New Shelf
        </button>
      </div>

      {editingId !== null && (
        <div style={{
          border: '1px solid var(--border)', borderRadius: '12px', padding: '14px',
          marginBottom: '16px', backgroundColor: 'var(--bg-card)'
        }}>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <input
              value={draft.name}
              onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
              placeholder="Shelf name (e.g. Living Room Bookcase)"
              style={{ flex: '1 1 220px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}
            />
            <input
              value={draft.location_label}
              onChange={e => setDraft(d => ({ ...d, location_label: e.target.value }))}
              placeholder="Location (e.g. Bedside Table)"
              style={{ flex: '1 1 220px', padding: '8px 10px', borderRadius: '8px', border: '1px solid var(--border)' }}
            />
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>Style:</span>
            {Object.entries(WOOD_THEMES).map(([key, t]) => (
              <button
                key={key}
                onClick={() => setDraft(d => ({ ...d, style_key: key }))}
                title={t.label}
                aria-label={t.label}
                style={{
                  width: '24px', height: '24px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                  backgroundColor: t.surface,
                  outline: key === draft.style_key ? '2px solid var(--accent)' : '1px solid var(--border)'
                }}
              />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={saveDraft} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: 'none', backgroundColor: 'var(--accent)', color: '#fff', cursor: 'pointer' }}>
              <Save size={14} /> Save
            </button>
            <button onClick={cancelEdit} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer' }}>
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      {shelves.length === 0 && editingId === null && (
        <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-soft)' }}>
          No shelves yet. Create one to start organizing your physical/virtual library by real-world location.
        </div>
      )}

      {activeShelf && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div>
              <h4 style={{ margin: 0 }}>{activeShelf.name}</h4>
              {activeShelf.location_label && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.8rem', color: 'var(--text-soft)' }}>
                  <MapPin size={12} /> {activeShelf.location_label}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={() => startEdit(activeShelf)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer' }} title="Edit shelf">
                <Pencil size={14} />
              </button>
              <button onClick={() => deleteShelf(activeShelf.id)} style={{ padding: '6px', borderRadius: '8px', border: '1px solid var(--border)', backgroundColor: 'transparent', cursor: 'pointer', color: '#c0392b' }} title="Delete shelf">
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {allShelfBooks.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px', marginBottom: '10px',
              backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px'
            }}>
              <Search size={14} color="var(--text-soft)" style={{ flexShrink: 0 }} />
              <input
                type="text"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                placeholder={`Search ${activeShelf.name}...`}
                style={{ flex: 1, border: 'none', outline: 'none', background: 'none', fontSize: '0.85rem', color: 'var(--text-main)' }}
              />
              {searchInput && (
                <button onClick={() => setSearchInput('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-soft)', display: 'flex', padding: 0 }} title="Clear search">
                  <X size={13} />
                </button>
              )}
            </div>
          )}

          <div style={{
            backgroundColor: activeTheme.surface,
            borderRadius: '10px',
            padding: '16px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            minHeight: '90px',
            boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.25)'
          }}>
            {allShelfBooks.length === 0 && (
              <div style={{ color: activeTheme.spineText, opacity: 0.7, fontSize: '0.85rem', padding: '8px' }}>
                No books assigned to this shelf yet. Assign one from a book's detail view.
              </div>
            )}
            {allShelfBooks.length > 0 && shelfBooks.length === 0 && (
              <div style={{ color: activeTheme.spineText, opacity: 0.7, fontSize: '0.85rem', padding: '8px' }}>
                No books on this shelf match your search.
              </div>
            )}
            {shelfBooks.map((book, i) => (
              <div key={book.id} style={{ position: 'relative' }}>
                <div
                  onClick={() => onBookClick && onBookClick(book)}
                  title={book.title}
                  style={{
                    width: '46px',
                    minHeight: '70px',
                    backgroundColor: activeTheme.spines[i % activeTheme.spines.length],
                    color: activeTheme.spineText,
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    writingMode: 'vertical-rl',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    padding: '6px 3px',
                    cursor: onBookClick ? 'pointer' : 'default',
                    boxShadow: '1px 1px 3px rgba(0,0,0,0.3)',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {book.title}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setMoveMenuBookId(moveMenuBookId === book.id ? null : book.id); }}
                  title="Move to another shelf"
                  style={{
                    position: 'absolute', top: '-8px', right: '-8px',
                    width: '20px', height: '20px', borderRadius: '50%',
                    border: '1px solid var(--border)', backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-soft)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                  }}
                >
                  <MoveRight size={11} />
                </button>
                {moveMenuBookId === book.id && (
                  <div style={{
                    position: 'absolute', top: '18px', left: '0', zIndex: 20,
                    backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)',
                    borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    minWidth: '140px', overflow: 'hidden'
                  }}>
                    {shelves.filter(s => s.id !== activeShelf.id).map(s => (
                      <div
                        key={s.id}
                        onClick={(e) => { e.stopPropagation(); moveBookToShelf(book.id, s.id); }}
                        style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', color: 'var(--text-main)', whiteSpace: 'nowrap' }}
                      >
                        {s.name}
                      </div>
                    ))}
                    <div
                      onClick={(e) => { e.stopPropagation(); moveBookToShelf(book.id, null); }}
                      style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', color: 'var(--text-soft)', borderTop: '1px solid var(--border)', whiteSpace: 'nowrap' }}
                    >
                      Unassign
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyShelves;
